import { Injectable } from '@angular/core';
import { GlobalFilterService } from '@shared-lib/services/global-filter-service';

@Injectable({
  providedIn: 'root',
})
export class ChartFilterHandlerService {

  private globalColorMap = new Map<string, string>();

  constructor(protected globalFilterService: GlobalFilterService) {}


  /**
   * Color Consistency Methods
   */
  registerItemColor(item: string, color: string): void {
    if (item && color && !this.globalColorMap.has(item)) {
      this.globalColorMap.set(item, color);
    }
  }

  getItemColor(item: string): string | undefined {
    return this.globalColorMap.get(item);
  }

  clearColorMap(): void {
    this.globalColorMap.clear();
  }

  /**
   * Legend Click Wrapper
   */
  handleLegendClick(data: any, params: any): void {
    if (!data?.enableLegendClick) return;

    const legendKey = data.legendKey || 'legendKey';
    const listener = data.legendListener || data.legendListner; // support typo

    if (!listener) {
      console.warn('Legend click enabled but no listener defined', data);
      return;
    }

    const clickedLegend = params.name;
    const isSelected = params.selected ? params.selected[clickedLegend] : true;

    // Check if the legend was clicked off or on
    // Some charts emit selection state. If deselecting, we remove the filter.
    if (!isSelected) {
      this.globalFilterService.deleteKey(legendKey, false);
      return;
    }

    this.globalFilterService.updateFilterSelection(
      legendKey,
      [clickedLegend],
      false, // skipReload
      data.widgetId,
      true, // emit
      listener,
    );
  }

  /**
   * Label Click Wrapper (X-Axis)
   */
  handleLabelClick(data: any, params: any): void {
    if (!data?.enableLabelClick) return;

    const labelKey = data.labelKey || 'labelKey';
    const listener = data.labelListener || data.labelListner;

    if (!listener) {
      console.warn('Label click enabled but no listener defined', data);
      return;
    }

    const clickedLabel = params.value || params.name;
    if (!clickedLabel) return;

    // Toggle logic: if already selected, deselect it. Otherwise, select it.
    // X-axis clicks often behave as toggles.
    if (this.globalFilterService.isSelected(labelKey, clickedLabel)) {
      this.globalFilterService.deleteKey(labelKey, false);
      // It might be better to reset with listener to target specific charts
      // this.globalFilterService.resetFilterSelection(labelKey, false, listener);
    } else {
      this.globalFilterService.updateFilterSelection(
        labelKey,
        [clickedLabel],
        false,
        data.widgetId,
        true,
        listener,
      );
    }
  }

  /**
   * Double X-Axis Click Handler
   * Triggered when a user clicks on the secondary (group) x-axis label.
   * Uses `doublexAxisKey` from config as the filter key for the API call.
   */
  handleDoubleXAxisClick(data: any, clickedValue: string): void {
    if (!data?.enableDoubleXAxis || !data?.enableXAxis2Click) return;

    const filterKey = data.doublexAxisKey;
    if (!filterKey || !clickedValue) return;

    const listener =
      data?.xAxis2ClickListener ||
      data?.labelListener ||
      data?.labelListner ||
      data?.filterListener ||
      data?.filterListner;

    // Toggle logic: if already selected, deselect; otherwise select
    if (this.globalFilterService.isSelected(filterKey, String(clickedValue))) {
      this.globalFilterService.resetFilterSelection(filterKey, false, listener);
    } else {
      this.globalFilterService.updateFilterSelection(
        filterKey,
        [clickedValue],
        false,
        data.widgetId,
        true,
        listener,
      );
    }
  }

  /**
   * Double Y-Axis Click Handler
   * Triggered when a user clicks on the secondary (right-side) y-axis label.
   * Uses `doubleyAxisKey` from config as the filter key, falls back to `doublexAxisKey`.
   */
  handleDoubleYAxisClick(data: any, clickedValue: string): void {
    if (!data?.enableDoubleYAxis || !data?.enableYAxis2Click) return;

    const filterKey = data.doubleyAxisKey || data.doublexAxisKey;
    if (!filterKey || !clickedValue) return;

    const listener =
      data?.yAxis2ClickListener ||
      data?.labelListener ||
      data?.labelListner ||
      data?.filterListener ||
      data?.filterListner;

    // Toggle logic: if already selected, deselect; otherwise select
    if (this.globalFilterService.isSelected(filterKey, String(clickedValue))) {
      this.globalFilterService.resetFilterSelection(filterKey, false, listener);
    } else {
      this.globalFilterService.updateFilterSelection(
        filterKey,
        [clickedValue],
        false,
        data.widgetId,
        true,
        listener,
      );
    }
  }

  /**
   * Checks if the chart should reload based on the filter event.
   * Logic:
   * 1. skipReload flag → never reload (initial trigger).
   * 2. filterListener present → reload only if this widgetId is in the list.
   * 3. No filterListener AND a widgetId is present → this is a chart-click with no targets, skip.
   * 4. No filterListener AND no widgetId → global dropdown change, reload all.
   */
  shouldReload(filters: any, widgetId: string): boolean {
    // Initial trigger — charts already loaded via ngAfterViewInit.
    if (filters?.skipReload) return false;

    const listenerData = filters?.filterListener || filters?.filterListner;
    if (listenerData) {
      // A targeted event: only reload if this widgetId is explicitly listed.
      const listeners = Array.isArray(listenerData)
        ? listenerData
        : listenerData.split(',').map((s: string) => s.trim());

      const willReload = listeners.includes(widgetId);

      return willReload;
    }

    // A chart click with no filterListener set means it has no targeted listeners.
    // Do NOT reload any chart — the emitting chart forgot to configure targets.
    // Only reload all when it's a global dropdown change (no widgetId).
    if (filters?.widgetId) {
      return false;
    }

    return true;
  }

  /**
   * Reset/remove filters for the provided widget data.
   * Accepts widget config (with `filterKey` as string or array) or a plain key string.
   */
  resetFilterSelection(dataOrKey: any): void {
    if (!dataOrKey) return;
    let keys: string[] = [];
    if (typeof dataOrKey === 'string') {
      keys = [dataOrKey];
    } else if (Array.isArray(dataOrKey)) {
      keys = dataOrKey;
    } else if (dataOrKey.filterKey) {
      keys = Array.isArray(dataOrKey.filterKey)
        ? dataOrKey.filterKey
        : String(dataOrKey.filterKey).split(',').map((k: string) => k.trim()).filter((k: string) => k);
    }

    if (!keys.length) return;

    keys.forEach((key, index) => {
      const isLast = index === keys.length - 1;
      if (isLast) {
        this.globalFilterService.resetFilterSelection(key, false);
      } else {
        this.globalFilterService.deleteKey(key, false);
      }
    });
  }

  /**
   * Handles user interaction (click) on the chart.
   * Logic:
   * 1. Extracts keys (string or array).
   * 2. Extracts listener config (supporting typo).
   * 3. Calls GlobalFilterService to update filter state.
   */
  updateFilterSelection(data: any, name: string | any | any[], clickedRawData?: any): void {

    console.log("lclick 22 inside updateFilterSelection chart-filter-handler", data, name);

    console.log('inside updateFilterSelection chart-filter-handler', data, name);

    if (!data?.filterKey) return;

    // Support both spellings: filterListener and filterListner
    const listener = data?.filterListener || data?.filterListner;
    console.log("lclick 23 inside updateFilterSelection chart-filter-handler listener is ", listener);

    // Normalize filter keys to an array
    const filterKeys: string[] = Array.isArray(data.filterKey)
      ? data.filterKey
      : (data.filterKey || '')

        .split(',')
        .map((k: string) => k.trim())
        .filter((k: string) => k);


    // Empty array means explicit deselection — always toggle OFF (remove filters)
    if (Array.isArray(name) && name.length === 0) {
      filterKeys.forEach((key, index) => {
        const isLast = index === filterKeys.length - 1;
        if (isLast) {
          this.globalFilterService.resetFilterSelection(key, false, listener);
        } else {
          this.globalFilterService.deleteKey(key, false);
        }
      });
      return;
    }

    // Determine if we should toggle OFF (remove) or ON (update)
    // Rule: If ALL keys are currently selected with the same value, then we remove them.
    // Otherwise, we update/set them.
    let allSelected = true;

    // We need to store the values we intend to set so we don't re-calculate them
    const keyValues: { [key: string]: any } = {};

    for (const key of filterKeys) {
      // Extract value:
      // If 'name' is an object and has the key, use that.
      // Otherwise fallback to 'name' itself (primitive case).
      let value = name;
      if (name && typeof name === 'object' && !Array.isArray(name) && key in name) {
        value = name[key];
      }

      keyValues[key] = value;

      // Check if currently selected
      // isSelected expects a string; compare against the string form of value
      if (!this.globalFilterService.isSelected(key, String(value))) {
        allSelected = false;
      }
    }
    console.log("lclick 25 inside updateFilterSelection chart-filter-handler allSelected is ", allSelected);

    // Compute per-listener extra params from filterKeyMap (or listenerFilterKeys) config.
    // Maps specific listener widgetIds to ADDITIONAL filter keys they should receive.
    // e.g. "filterKeyMap": { "widget-bar": ["abo_group_name_c", "region"] }
    const filterKeyMapConfig = data?.filterKeyMap || data?.listenerFilterKeys;
    let listenerExtraParams: { [widgetId: string]: { [key: string]: any } } | undefined;

    if (filterKeyMapConfig && typeof filterKeyMapConfig === 'object') {
      const rawData = clickedRawData || (name && typeof name === 'object' && !Array.isArray(name) ? name : null);

      listenerExtraParams = {};
      for (const [targetWidgetId, config] of Object.entries(filterKeyMapConfig)) {
        const extraParams: { [key: string]: any } = {};

        if (Array.isArray(config)) {
          // Array format: extra key names match data field names
          for (const extraKey of config as string[]) {
            if (filterKeys.includes(extraKey)) continue;
            let value: any;
            if (rawData && typeof rawData === 'object' && extraKey in rawData) {
              value = rawData[extraKey];
            } else {
              value = name;
            }
            extraParams[extraKey] = Array.isArray(value) ? value : [value];
          }
        } else if (config && typeof config === 'object') {
          // Object mapping: { filterKeyName: dataFieldName }
          for (const [filterKey, dataField] of Object.entries(config as Record<string, string>)) {
            if (filterKeys.includes(filterKey)) continue;
            let value: any;
            if (rawData && typeof rawData === 'object' && dataField in rawData) {
              value = rawData[dataField];
            } else {
              value = name;
            }
            extraParams[filterKey] = Array.isArray(value) ? value : [value];
          }
        }

        if (Object.keys(extraParams).length > 0) {
          listenerExtraParams[targetWidgetId] = extraParams;
        }
      }
      if (Object.keys(listenerExtraParams).length === 0) {
        listenerExtraParams = undefined;
      }
    }

    if (allSelected) {
      console.log("lclick 26 inside updateFilterSelection chart-filter-handler allSelected is true");
      // Toggle OFF: Remove all filters
      filterKeys.forEach((key, index) => {
        console.log("lclick 28 inside updateFilterSelection chart-filter-handler filterKeys.forEach loop");
        const isLast = index === filterKeys.length - 1;
        if (isLast) {
          // Pass listener so only targeted charts reload, not ALL charts
          this.globalFilterService.resetFilterSelection(key, false, listener);
        } else {
          this.globalFilterService.deleteKey(key, false);
        }
      });
    } else {
      console.log("lclick 27 inside updateFilterSelection chart-filter-handler allSelected is false");
      // Toggle ON: Update all filters
      filterKeys.forEach((key, index) => {
        console.log("lclick 29 inside updateFilterSelection chart-filter-handler filterKeys.forEach loop");
        const val = keyValues[key];
        // GlobalFilterService.updateFilterSelection expects an array of values
        const valArray = Array.isArray(val) ? val : [val];
        const isLast = index === filterKeys.length - 1;
        // We call updateFilterSelection for each key individually.
        // emit only on last.
        // Pass 'listener' to respect dashboard config (targeted updates).
        this.globalFilterService.updateFilterSelection(
          key,
          valArray,
          false,
          data.widgetId,
          isLast,
          listener,
          isLast ? listenerExtraParams : undefined,
        );
      });
    }
  }
}
